import React from 'react'
import { generatePath, Link } from 'react-router-dom'
import { useQueryParam, StringParam } from 'use-query-params'

import { Space, Button, Input, Upload, notification } from 'antd'
import * as Icons from '@ant-design/icons'

import { Layout } from 'admin/lib/Layouts'
import { Breadcrumb, BreadcrumbArrayItem } from 'admin/lib/Breadcrumbs'
import { ScreenSpin } from 'admin/lib/ScreenSpin'
import * as ROUTES from 'admin/routes'

import { Queries, useApi } from 'admin/lib/Api'

import { CertificatesList } from './CertificatesList'

import { uploadToSasLink } from '@qflow/api'
import { exportFullCsv } from 'admin/lib/Export/exportFullCsv'

export function Certificates() {
  const [searchTerm, setSearchTerm] = useQueryParam('search', StringParam)

  const certificates = Queries.useCertificates()

  const { handleUpload } = useCertificateImport()

  const filteredCertificates = certificates.data
    ? searchTerm
      ? certificates.data.filter(
          cert =>
            cert.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            cert.certificateNumber
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            cert.certificationScheme
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
        )
      : certificates.data
    : []

  return (
    <Layout
      header={
        <>
          <Breadcrumb
            items={[
              {
                title: (
                  <BreadcrumbArrayItem href={ROUTES.REFERENCE_DATA.INDEX}>
                    Reference Data
                  </BreadcrumbArrayItem>
                )
              },
              {
                title: 'Certificates'
              }
            ]}
          />

          <Space>
            <Input.Search
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value, 'replace')}
              style={{ maxWidth: '20rem' }}
            />

            <Link to={generatePath(ROUTES.CERTIFICATES.REGISTER)}>
              <Button type="primary" icon={<Icons.FolderAddOutlined />}>
                New
              </Button>
            </Link>

            <Upload
              accept=".csv"
              beforeUpload={file => {
                handleUpload(file)
                return false
              }}
              showUploadList={false}
            >
              <Button icon={<Icons.UploadOutlined />}>Import</Button>
            </Upload>

            <Button
              icon={<Icons.DownloadOutlined />}
              onClick={() =>
                exportFullCsv(certificates.data, [
                  'eTag',
                  'timestamp',
                  'createdAtDate'
                ])
              }
            >
              Export
            </Button>
          </Space>
        </>
      }
    >
      {!certificates.isFetched && <ScreenSpin />}

      {certificates.isError && (
        <div>
          Something went wrong:{' '}
          {String(certificates.error.response?.data ?? certificates.error)}
        </div>
      )}

      {certificates.isFetched && certificates.data && (
        <CertificatesList certificates={filteredCertificates} />
      )}
    </Layout>
  )
}

function useCertificateImport() {
  const api = useApi()

  async function handleUpload(file) {
    const IN_PROGRESS_KEY = 'handleUpload/inProgress'

    notification.info({
      key: IN_PROGRESS_KEY,
      message: 'Starting import',
      icon: <Icons.LoadingOutlined />,
      duration: 0
    })

    try {
      const [sasURI, getUriStatus, getUriError] =
        await api.getUploadCertificateLink()
      if (getUriError) {
        const error = new Error('Error Generating Import link')
        error.message = 'Error Generating Import link'
        error.description = (
          <>
            <p>Error, you may wish to try again. </p>
            <p>{`Details: ${getUriStatus}: ${getUriError}`}</p>
          </>
        )
        throw error
      }

      const [, uploadStatus, uploadError] = await uploadToSasLink(sasURI, file)
      if (uploadError) {
        const error = new Error('Error Uploading Certificates')
        error.message = 'Error Uploading Certificates'
        error.description = (
          <>
            <p>Error, you may wish to try again. </p>
            <p>{`Details: ${uploadStatus}: ${uploadError}`}</p>
          </>
        )
        throw error
      }

      const [, queueStatus, queueError] =
        await api.queueReferenceDataImport('certificates')
      if (queueError) {
        const error = new Error('Error Queueing Import')
        error.message = 'Error Queueing Import'
        error.description = (
          <>
            <p>Error, you may wish to try again. </p>
            <p>{`Details: ${queueStatus}: ${queueError}`}</p>
          </>
        )
        throw error
      }

      notification.success({
        message: 'Success! 💪😎🍹',
        description:
          'Certificates Imported. Values might take some time to reflect'
      })
    } catch (e) {
      if (e.message && e.description) {
        notification.error({
          message: e.message,
          description: e.description
        })
      } else {
        notification.error({
          message: 'unknown error ☹️',
          description: String(e)
        })
      }
    } finally {
      notification.destroy(IN_PROGRESS_KEY)
    }
  }

  return {
    handleUpload
  }
}
